import type { StateCreator } from 'zustand';
import type { Satellite, SatelliteLead, ForkSatelliteRequest } from '../types';
import { satellitesApi } from '../services/satellitesApi';

export interface SatellitesSlice {
  satellites: Satellite[];
  activeSatellite: Satellite | null;
  satelliteLeads: SatelliteLead[];
  satellitesLoading: boolean;

  fetchSatellites: (status?: string, originStrategyId?: number) => Promise<void>;
  fetchSatellite: (id: number) => Promise<void>;
  forkSatellite: (data: ForkSatelliteRequest) => Promise<{ satellite: Satellite; strategy_id: number; brand_id: number }>;
  updateSatellite: (id: number, data: Partial<Satellite>) => Promise<void>;
  archiveSatellite: (id: number) => Promise<void>;
  updateSatelliteStatus: (id: number, status: string) => Promise<void>;
  fetchSatelliteLeads: (satelliteId: number, status?: string) => Promise<void>;
  addSatelliteLead: (satelliteId: number, data: Partial<SatelliteLead>) => Promise<SatelliteLead>;
  updateSatelliteLead: (satelliteId: number, leadId: number, data: Partial<SatelliteLead>) => Promise<void>;
  promoteSatelliteLead: (satelliteId: number, leadId: number) => Promise<number>;
  linkSatelliteAccount: (satelliteId: number, accountId: number) => Promise<void>;
}

export const createSatellitesSlice: StateCreator<SatellitesSlice> = (set) => ({
  satellites: [],
  activeSatellite: null,
  satelliteLeads: [],
  satellitesLoading: false,

  fetchSatellites: async (status, originStrategyId) => {
    set({ satellitesLoading: true });
    try {
      const sats = await satellitesApi.list({
        status,
        origin_strategy_id: originStrategyId,
      });
      set({ satellites: sats });
    } finally {
      set({ satellitesLoading: false });
    }
  },

  fetchSatellite: async (id) => {
    set({ satellitesLoading: true });
    try {
      const sat = await satellitesApi.get(id);
      set({ activeSatellite: sat });
    } finally {
      set({ satellitesLoading: false });
    }
  },

  forkSatellite: async (data) => {
    set({ satellitesLoading: true });
    try {
      const result = await satellitesApi.fork(data);
      const sats = await satellitesApi.list();
      set({ satellites: sats, activeSatellite: result.satellite });
      return result;
    } finally {
      set({ satellitesLoading: false });
    }
  },

  updateSatellite: async (id, data) => {
    const updated = await satellitesApi.update(id, data);
    set((state) => ({
      satellites: state.satellites.map((s) => (s.id === id ? updated : s)),
      activeSatellite: state.activeSatellite?.id === id ? updated : state.activeSatellite,
    }));
  },

  archiveSatellite: async (id) => {
    await satellitesApi.delete(id);
    set((state) => ({
      satellites: state.satellites.filter((s) => s.id !== id),
      activeSatellite: state.activeSatellite?.id === id ? null : state.activeSatellite,
    }));
  },

  updateSatelliteStatus: async (id, status) => {
    const updated = await satellitesApi.updateStatus(id, status);
    set((state) => ({
      satellites: state.satellites.map((s) => (s.id === id ? updated : s)),
      activeSatellite: state.activeSatellite?.id === id ? updated : state.activeSatellite,
    }));
  },

  fetchSatelliteLeads: async (satelliteId, status) => {
    const leads = await satellitesApi.listLeads(satelliteId, status);
    set({ satelliteLeads: leads });
  },

  addSatelliteLead: async (satelliteId, data) => {
    const lead = await satellitesApi.addLead(satelliteId, data);
    set((state) => ({ satelliteLeads: [lead, ...state.satelliteLeads] }));
    return lead;
  },

  updateSatelliteLead: async (satelliteId, leadId, data) => {
    const updated = await satellitesApi.updateLead(satelliteId, leadId, data);
    set((state) => ({
      satelliteLeads: state.satelliteLeads.map((l) => (l.id === leadId ? updated : l)),
    }));
  },

  promoteSatelliteLead: async (satelliteId, leadId) => {
    const result = await satellitesApi.promoteLead(satelliteId, leadId);
    set((state) => ({
      satelliteLeads: state.satelliteLeads.map((l) =>
        l.id === leadId ? result.satellite_lead : l
      ),
    }));
    return result.crm_lead_id;
  },

  linkSatelliteAccount: async (satelliteId, accountId) => {
    const updated = await satellitesApi.linkAccount(satelliteId, accountId);
    set((state) => ({
      satellites: state.satellites.map((s) => (s.id === satelliteId ? updated : s)),
      activeSatellite: state.activeSatellite?.id === satelliteId ? updated : state.activeSatellite,
    }));
  },
});
